import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding #18: An abstract React transition component",
  "description": "",
  "published": "2016-08-07T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-18-an-abstract-react-transition-component/swizec/6906"],
  "categories": "d3js, Front End, Livecoding, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding ?. Always under 500 words and with pictures. You can follow my channel, `}<a parentName="em" {...{
          "href": "https://livecoding.tv/swizec"
        }}>{`here`}</a>{`. New content almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{`. There’s live chat, come say hai ?`}</em></p>
    <p>{`This week we made a circle bounce back and forth. ?`}</p>
    <p>{`Trivial, right? Yes, as trivial as it gets. The bouncing circle wasn’t the point, the point was how it’s made. This: `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-transitionable-component"
      }}>{`react-transitionable-component`}</a>{`. My first not-just-a-cool-experiment open source contribution to the React ecosystem.`}</p>
    <p>{`It’s an abstract component that makes building transitions easy.`}</p>
    <p>{`Over the past few weeks, I’ve noticed that making a transition always follows the same pattern: move props to state, render from state instead of props, use `}<inlineCode parentName="p">{`d3.transition`}</inlineCode>{` to transition prop values on every `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{`. What if we can abstract all that work away? ?`}</p>
    <p>{`We started with this jumping circle:`}</p>
    <blockquote>
      <p parentName="blockquote"><a parentName="p" {...{
          "href": "//imgur.com/sFBlt"
        }}></a></p>
    </blockquote>
    <p>{`And ended with this transitioned circle:`}</p>
    <blockquote>
      <p parentName="blockquote"><a parentName="p" {...{
          "href": "//imgur.com/Z256F"
        }}></a></p>
    </blockquote>
    <p>{`It took me over an hour to realize that zooming-in would have made everyone’s lives easier, but here we are. Tiny little gifs for tiny little screens. Or big screens. The gifs are always tiny.`}</p>
    <p>{`Making a circle that transitions like that isn’t hard. It takes about twenty minutes to show and explain to a novice in both React and D3. At least that’s how much it takes when I do workshops.`}</p>
    <p>{`So who cares about making abstraction, right? I care. Doing it from scratch every time gets old, fast. I wanted a way to say `}<em parentName="p">{`“make this component use transitions for everything.”`}</em>{` without worrying about implementation every time.`}</p>
    <p>{`As you can expect from a project that took less than two hours from start to finish, there’s little code to show. `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-transitionable-component/blob/master/src/TransitionableComponent.js"
      }}>{`40 lines`}</a>{` that look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class TransitionableComponent extends Component {
  constructor(props) {
    // copy all props to state
    // call _defineEasing and _defineDuration
  }

  _defineEasing(easing) {
    // if this.easing undefined, get easing function from d3
  }

  _defineDuration(duration) {
    // if this.duration undefined, set it
  }

  componentWillReceiveProps(newProps) {
    this._defineEasing(newProps.easing);
    this._defineDuration(newProps.duration);

    const node = d3.select(this.refs.node);

    let transition = node.transition().ease(this.easing);

    if (this.duration !== undefined) {
      transition.duration(this.duration);
    }

    Object.keys(newProps).forEach((k) => {
      transition.attr(k, newProps[k]);
    });

    transition.on("end", () => this.setState(newProps));
  }
}
`}</code></pre>
    <p>{`I cut out the boring bits. You can see them on GitHub, `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-transitionable-component/blob/master/src/TransitionableComponent.js"
      }}>{`here`}</a>{`.`}</p>
    <p>{`The fun bit is in `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{`. We update our easing function and our duration, which should’ve been dynamic getters instead. Then we get the `}<inlineCode parentName="p">{`node`}</inlineCode>{` and start a `}<inlineCode parentName="p">{`transition()`}</inlineCode>{` with an easing function, and a `}<inlineCode parentName="p">{`duration`}</inlineCode>{`, if it’s there. After that, we walk through every prop and add it to the pile of transitioning attributes with `}<inlineCode parentName="p">{`.attr`}</inlineCode>{`. When the transition is over, we use the `}<inlineCode parentName="p">{`'end'`}</inlineCode>{` callback to update component state and ensure React understands what’s going on.`}</p>
    <p>{`You’ll notice `}<inlineCode parentName="p">{`TransitionableComponent`}</inlineCode>{` doesn’t have a `}<inlineCode parentName="p">{`render()`}</inlineCode>{` function. It’s an abstract component meant to be extended by a real component. Something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class Circle extends TransitionableComponent {
    render() {
        return <circle cx={this.state.cx} cy={this.state.cy} r={this.state.r}>
    }
}
</circle>
`}</code></pre>
    <p>{`That’s a transitionable circle now. Any props passed into it are transitioned. You have to be careful to use `}<inlineCode parentName="p">{`this.state`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`this.props`}</inlineCode>{` when using them though.`}</p>
    <p>{`Using the `}<inlineCode parentName="p">{`Circle`}</inlineCode>{` component looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<circle cx="100" cy="100" r="5" easing="cubicInOut" duration="1500"></circle>
`}</code></pre>
    <p>{`See, easy. :)`}</p>
    <p>{`Join me next week when we clean this up to use dynamic getters, avoid importing the entire d3 library, and build some examples.`}</p>
    <p>{`PS: the edited and improved versions of these videos are becoming a video course. Readers of the engineer package of `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js/"
      }}>{`React+d3js ES6`}</a>{` get the video course for free when it’s ready.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      